import {
  doc, collection, addDoc, updateDoc, deleteDoc, onSnapshot,
  query, orderBy, limit, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

// ── COMMENTS ON A PIECE OF WORK ──────────────────────────────────────────────
//
// A SUBCOLLECTION, photos/{photoId}/comments/{commentId}, not an array on the
// photo. Three reasons, in order of how much they matter here:
//
//   • The photo document carries its THUMBNAIL inline (Storage isn't
//     provisioned — see galleryService), so it is already heavy. Comments on
//     it would be re-read in full by the feed, which fetches these documents
//     in batches of thirty, on every visit.
//   • Rules can pin ownership per comment. An array is one field: anyone
//     allowed to append is allowed to rewrite the lot.
//   • A comment is independently editable and deletable, which is exactly
//     what a document is for.
//
// THE COUNT IS NOT WRITTEN HERE. `commentsCount` on the photo is maintained by
// a Cloud Function (functions/onCommentWrite.js) and is `allow update: if
// false` for clients — a counter a client can move is a counter that lies. The
// feed reads it for free, since it already fetches those documents.

/** Longest a comment may be. Mirrored in firestore.rules — keep them equal. */
export const COMMENT_MAX = 1000;

/** Newest-first is wrong for a conversation; a thread reads top to bottom. */
const PAGE = 200;

// ── WHICH GALLERY ITEM ────────────────────────────────────────────────────────
// Comments hang off the PUBLIC gallery document, and there are two kinds of
// those: 'photos' and 'publishedDesigns'. Both have the same shape and the same
// owner field, so one set of functions serves both — the collection is passed
// in rather than baked in, which is what let 3D designs gain comments without a
// parallel copy of this file, its rules and its triggers.
const colRef = (col, id) => collection(db, col, id, 'comments');

/**
 * Live comments for ONE open photo.
 *
 * A listener rather than a fetch, for the same reason SocialBar uses one: this
 * only ever runs while a photo is open on screen, and watching your own
 * comment appear is the feature. Grids and feeds never call this — they read
 * `commentsCount`, which costs nothing extra.
 *
 * Returns the unsubscribe function.
 */
export function subscribeComments(col, id, cb) {
  if (!col || !id) return () => {};
  return onSnapshot(
    query(colRef(col, id), orderBy('createdAt', 'asc'), limit(PAGE)),
    (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (err) => { console.warn('[comments] subscribe failed:', err?.code || err); cb([]); },
  );
}

/**
 * Posts a comment, or a reply when `parentId` is given.
 *
 * THE AUTHOR'S NAME IS STORED ON THE COMMENT, their avatar is not. A customer's
 * user document is private (see firestore.rules), so a reader cannot look up
 * who wrote this — the name has to travel with the comment or it cannot be
 * shown at all. The avatar is a data URL of up to 120KB (constants/designer);
 * copying that onto every comment would make reading a thread cost megabytes,
 * so it is resolved at render time from the designer cache instead, and
 * commenters without a public profile show the default person icon.
 *
 * ONE LEVEL OF THREADING. A reply to a reply attaches to the same parent, so a
 * thread cannot nest into a staircase on a phone. The UI enforces it too, but
 * this is the reason the field is a parent id and not a path.
 */
export async function addComment(col, id, { uid, name, text, parentId = null, replyToUid = null }) {
  const body = String(text || '').trim();
  if (!col || !id || !uid) throw new Error('not-signed-in');
  if (!body) throw new Error('empty');
  if (body.length > COMMENT_MAX) throw new Error('too-long');

  const ref = await addDoc(colRef(col, id), {
    uid,
    name: String(name || '').slice(0, 80),
    text: body,
    parentId: parentId || null,
    // WHO IS BEING ANSWERED, which `parentId` does not tell you.
    //
    // A reply to a reply attaches to the THREAD'S ROOT (see the note above), so
    // `parentId` names the top-level comment however deep in the conversation
    // you were. The notification trigger read the root's author from it and
    // therefore told the wrong person — and told nobody at all when the root
    // was your own, which is the commonest case in a back-and-forth.
    //
    // The UI knows exactly whose words you pressed Reply under; this records
    // it. Only ever a uid, so it cannot misattribute anything.
    ...(replyToUid ? { replyToUid: String(replyToUid).slice(0, 64) } : {}),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * Edits your own comment. Only the text moves; `editedAt` is what the UI shows
 * an "edited" marker from, and the rules allow no other field to change.
 */
export async function editComment(col, id, commentId, text) {
  const body = String(text || '').trim();
  if (!body) throw new Error('empty');
  if (body.length > COMMENT_MAX) throw new Error('too-long');
  await updateDoc(doc(db, col, id, 'comments', commentId), {
    text: body,
    editedAt: serverTimestamp(),
  });
}

/**
 * Deletes a comment. Allowed for its author AND for the owner of the photo —
 * moderating your own page is not optional, and the rules grant both.
 *
 * Replies to a deleted comment are NOT cascaded. Doing that from the client
 * would be a query plus N deletes, run by whoever happened to press the
 * button, with no guarantee it finished. The UI shows an orphaned reply under
 * a "comment removed" stub instead, which is honest and costs nothing.
 */
export async function removeComment(col, id, commentId) {
  await deleteDoc(doc(db, col, id, 'comments', commentId));
}
